import { useEffect, useRef } from "react";
import { logger } from "../../utils/Logger";
import useStore from "../../Store/Store";
import { theme } from "../../Themes/Themes";
import { getConnectedCellIds } from "../../utils/DiagramUtils";

export const fixCellVisibilityBeforeSave = async () => {
  try {
    const { repoData, cellToPath } = useStore.getState();

    // Get current diagram XML to check for cells with visible="0"
    const xmlResponse = await useStore.getState().postToDrawioWaitForResponse({
      action: "GET_DIAGRAM_XML",
      data: {},
    });

    if (xmlResponse.status !== "SUCCESS" || !xmlResponse.data?.xml) {
      logger.info("fixCellVisibilityBeforeSave: Could not get diagram XML");
      return;
    }

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlResponse.data.xml, "text/xml");
    const cellElements = xmlDoc.querySelectorAll('mxCell[visible="0"]');

    const cellsToMakeVisible = [];

    cellElements.forEach((cellElement) => {
      const cellId = cellElement.getAttribute("id");
      if (!cellId) return;

      // Check if we have repoData for this cell (either by cellId or via cellToPath)
      const path = cellToPath[cellId] || cellId;
      const repoDataEntry = repoData[path];

      if (repoDataEntry) {
        // If repoData exists and:
        // 1. visible is explicitly true, OR
        // 2. visible property doesn't exist (defaults to visible), OR
        // 3. we couldn't find the repoData object
        // Then we should make the cell visible
        if (repoDataEntry.visible !== false) {
          cellsToMakeVisible.push(cellId);
          logger.info(
            `fixCellVisibilityBeforeSave: Cell ${cellId} will be made visible based on repoData`,
            { path, visible: repoDataEntry.visible }
          );
        }
      } else {
        // No repoData found - default to making it visible
        cellsToMakeVisible.push(cellId);
        logger.info(
          `fixCellVisibilityBeforeSave: Cell ${cellId} will be made visible (no repoData found)`,
          { path }
        );
      }
    });

    // Apply visibility changes if needed
    if (cellsToMakeVisible.length > 0) {
      logger.info(
        `fixCellVisibilityBeforeSave: Making ${cellsToMakeVisible.length} cells visible based on repoData`
      );

      await useStore.getState().postToDrawioWaitForResponse({
        action: "CHANGE_CELLS_STYLE",
        data: {
          cellIds: cellsToMakeVisible,
          style: { visible: true },
          options: {
            permanentStyleChange: true,
          },
        },
      });
    }
  } catch (error) {
    logger.error(
      "Error fixing cell visibility before save: ",
      JSON.stringify(error)
    );
  }
};

export const resetTemporaryStyles = async () => {
  try {
    // First, fix cell visibility based on repoData before resetting styles
    await fixCellVisibilityBeforeSave();

    const response = await useStore.getState().postToDrawioWaitForResponse({
      action: "CHANGE_CELLS_STYLE",
      data: {
        options: {
          removeTemporaryStyles: true,
        },
      },
    });
    if (response.status === "SUCCESS") {
      if (!useStore.getState().simulationsState.selectedSimulationKey) {
        useStore.getState().setShowHideIrrelevantCellsToggle(false);
      }
    } else {
      throw new Error(response);
    }
  } catch (error) {
    logger.error("Error resetting temporary styles: ", JSON.stringify(error));
  }
};

export const DiagramHighlight = () => {
  const previousHighlightedCells = useRef([]);

  const { selectedCell, selectedSimulationKey } = useStore((state) => {
    return {
      selectedCell: state.selectedCell,
      selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    };
  });

  useEffect(() => {
    if (selectedSimulationKey) return;
    if (selectedCell) {
      applyHighlightWithConnections(selectedCell);
    } else {
      resetHighlight(previousHighlightedCells.current);
      previousHighlightedCells.current = []; // Clear previous highlights
    }
  }, [selectedCell, selectedSimulationKey]);

  const applyHighlightWithConnections = (cellId) => {
    const allCellsToHighlight = getConnectedCellIds(15); // Get connected cells grouped by levels

    // Reset styles for previously highlighted cells
    if (previousHighlightedCells.current.length > 0) {
      resetHighlight(previousHighlightedCells.current);
    }

    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "CHANGE_CELLS_STYLE",
        data: {
          cellIds: allCellsToHighlight,
          style: {
            stroke: theme.custom.translucentPink,
            strokeWidth: 1.5,
            visible: true,
            flowAnimation: 1,
          },
          options: {
            temporaryStyleChange: true,
          },
        },
      })
      .then(() => {
        previousHighlightedCells.current = allCellsToHighlight; // Update previously highlighted cells
        useStore.getState().setShowHideIrrelevantCellsToggle(true);
      })
      .catch((error) => {
        logger.error("Error applying highlight to connected cells: ", error);
      });
  };

  const resetHighlight = (cellIds) => {
    useStore
      .getState()
      .postToDrawioWaitForResponse({
        action: "CHANGE_CELLS_STYLE",
        data: {
          cellIds: cellIds,
          options: {
            removeTemporaryStyles: true,
          },
        },
      })
      .then((response) => {
        if (response?.status === "SUCCESS") {
          useStore.getState().setShowHideIrrelevantCellsToggle(false);
        }
      })
      .catch((error) => logger.error("Error resetting highlight: ", error));
  };

  return null; // This component doesn't render anything visible
};
